// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useRootNavigationState, useRouter } from 'expo-router';

import type { UserSession } from '@/app/user-context';
import { buildAuthUrl, exchangeCode, parseAuthCallback, REDIRECT_URI } from '@/auth/oidc';
import { consumePostLoginPath, storePostLoginPath } from '@/auth/postLoginPath';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useRoundState } from '@/stores/roundState';

WebBrowser.maybeCompleteAuthSession();

type UseAuthRedirectParams = {
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
  onError?: (message: string) => void;
  onSuccess?: () => void;
  debug?: boolean;
  returnTo?: string | null;
};

const buildErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const useAuthRedirect = ({ user, setUser, onError, onSuccess, debug, returnTo }: UseAuthRedirectParams) => {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const [loading, setLoading] = useState(false);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'auth-redirect',
      }),
    [apiBaseUrl, user?.token]
  );

  const handleAuthSuccess = useCallback(
    async (auth: { id: string; name: string; accessToken: string; idToken?: string | null; refreshToken?: string | null }) => {
      useRoundState.getState().clear();
      setUser({
        id: auth.id,
        forename: auth.name,
        token: auth.accessToken,
        idToken: auth.idToken ?? null,
        refreshToken: auth.refreshToken ?? null,
      });
      const storedReturnTo = await consumePostLoginPath();
      const nextRoute = storedReturnTo || `/welcome${debug ? '?debug=true' : ''}`;
      if (isNavigationReady) {
        router.replace(nextRoute);
      }
      onSuccess?.();
    },
    [debug, isNavigationReady, onSuccess, router, setUser]
  );

  const handleRedirect = useCallback(async (idpHint?: unknown) => {
    // idpHint may be a provider name (e.g. 'google'); guard against being called
    // directly as an onPress handler where the argument is a press event.
    const hint = typeof idpHint === 'string' ? idpHint : undefined;
    logToBff('login.redirect', { hasUser: !!user, idpHint: hint ?? null }, 'info');
    try {
      setLoading(true);
      await storePostLoginPath(returnTo ?? null);
      const url = await buildAuthUrl(hint ? { idpHint: hint } : undefined);
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        window.location.href = url;
        return;
      }
      const result = await WebBrowser.openAuthSessionAsync(url, REDIRECT_URI);
      if (result.type === 'success' && result.url) {
        const { code, state } = parseAuthCallback(result.url);
        if (!code) {
          onError?.('Inloggningen kunde inte slutföras.');
          return;
        }
        const auth = await exchangeCode(code, state ?? undefined);
        await handleAuthSuccess(auth);
      } else if (result.type === 'dismiss') {
        onError?.('Inloggningen avbröts.');
      }
    } catch (err) {
      onError?.(buildErrorMessage(err, 'Kunde inte starta inloggning.'));
    } finally {
      setLoading(false);
    }
  }, [handleAuthSuccess, logToBff, onError, returnTo, user]);

  return {
    loading,
    handleRedirect,
  };
};
